import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Alert
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import services from '../../services/services';

const FeedbackModal = ({ open, onClose, fileId, vendorName, submitterEmail }) => {
  const [isPositive, setIsPositive] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (isPositive === null) {
      setError('Please select thumbs up or thumbs down');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const feedbackData = {
        file_id: fileId,
        isPositive: isPositive,
        comment: comment,
        vendor_name: vendorName,
        submitter_email: submitterEmail
      };

      const result = await services.submitFeedback(feedbackData);
      console.log('Feedback submitted:', result);
      setSuccess(true);
      
      // Close modal after 1.5 seconds
      setTimeout(() => {
        onClose();
        resetForm();
      }, 1500);

    } catch (err) {
      console.error('Feedback submission error:', err);
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsPositive(null);
    setComment('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Feedback</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success">Thank you for your feedback!</Alert>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              How was your experience submitting this file?
            </Typography>

            <Box display="flex" justifyContent="center" gap={4} my={3}>
              {/* Thumbs Up */}
              <Box textAlign="center">
                <IconButton
                  onClick={() => setIsPositive(true)}
                  color={isPositive === true ? 'primary' : 'default'}
                  sx={{
                    border: isPositive === true ? '2px solid #4CAF50' : '1px solid #ccc',
                    borderColor: isPositive === true ? '#4CAF50' : '#ccc',
                    borderRadius: '50%',
                    padding: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.1)'
                    }
                  }}
                >
                  <ThumbUpIcon 
                    fontSize="large" 
                    sx={{ color: isPositive === true ? '#4CAF50' : 'inherit' }}
                  />
                </IconButton>
                <Typography variant="caption" display="block" mt={1}>
                  Good
                </Typography>
              </Box>

              {/* Thumbs Down */}
              <Box textAlign="center">
                <IconButton
                  onClick={() => setIsPositive(false)}
                  color={isPositive === false ? 'error' : 'default'}
                  sx={{
                    border: isPositive === false ? '2px solid #f44336' : '1px solid #ccc',
                    borderColor: isPositive === false ? '#f44336' : '#ccc',
                    borderRadius: '50%',
                    padding: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)'
                    }
                  }}
                >
                  <ThumbDownIcon 
                    fontSize="large"
                    sx={{ color: isPositive === false ? '#f44336' : 'inherit' }}
                  />
                </IconButton>
                <Typography variant="caption" display="block" mt={1}>
                  Bad
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comments (optional)"
              placeholder="Tell us more about your experience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
              margin="normal"
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || isPositive === null || success}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
